/**
 * FrameManager — lets the page's widget read iframes as part of the page.
 *
 *  - Same-origin iframes: runs a FrameAgent on the frame's document directly.
 *  - Iframes on another subdomain of the same root domain (or an origin in
 *    data-frame-origins) that load the ReadingVox script themselves: that copy
 *    says hello, we acknowledge, and it acts as an agent over postMessage.
 *  - Anything else (YouTube, Google, …) is ignored, as before.
 *
 * The page stays the single player and toolbar: frame text is read in place,
 * words are highlighted in whichever document holds the sentence, and
 * selections / click-and-listen in a frame play through the page.
 */
import type { WordHighlightInfo } from '../core/StreamingPlayer';
import { FrameAgent } from './FrameAgent';
import {
  FRAME_PROTOCOL_VERSION, RV_FRAME, isFrameMessage, originAllowed,
  type FrameRect, type HighlightStyle, type ToChild, type ToParent,
} from './protocol';

export interface FrameManagerOptions {
  /** Extra origins allowed for frame mode (besides the page's root domain) */
  frameOrigins?: string[];
  autoScroll: boolean;
  ignoreSelectors?: string[];
  /** Text selected in a frame, rect in this page's viewport (empty text = cleared) */
  onSelection(text: string, rect: FrameRect | null): void;
  /** Click-and-listen in a frame */
  onSpeak(text: string): void;
  debug?: boolean;
}

type Link =
  | { kind: 'local'; iframe: HTMLIFrameElement; agent: FrameAgent; doc: Document }
  | { kind: 'remote'; iframe: HTMLIFrameElement; win: Window; origin: string; text: string };

/** Frames too small to hold readable content (tracking pixels, share buttons) */
const MIN_W = 120;
const MIN_H = 40;

export class FrameManager {
  private links = new Map<HTMLIFrameElement, Link>();
  private watched = new WeakSet<HTMLIFrameElement>();
  private observer: MutationObserver | null = null;
  /** Frame whose document holds the sentence being highlighted */
  private active: Link | null = null;
  private style: HighlightStyle = {};
  private clickListen = false;

  constructor(private opts: FrameManagerOptions) {}

  start(): void {
    window.addEventListener('message', this.onMessage);
    document.querySelectorAll('iframe').forEach((f) => this.watch(f));
    this.inviteFrames();
    if (typeof MutationObserver !== 'undefined' && document.body) {
      this.observer = new MutationObserver((records) => {
        for (const r of records) {
          r.addedNodes.forEach((n) => {
            if (n instanceof HTMLIFrameElement) this.watch(n);
            else if (n instanceof Element) n.querySelectorAll('iframe').forEach((f) => this.watch(f));
          });
        }
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  destroy(): void {
    window.removeEventListener('message', this.onMessage);
    this.observer?.disconnect();
    this.links.forEach((l) => this.detach(l));
    this.links.clear();
  }

  /** Text of a frame for in-place reading (TextExtractor.frameText) */
  textFor = (iframe: HTMLIFrameElement): string | null => {
    let link = this.links.get(iframe);
    if (!link) {
      this.attachLocal(iframe); // loaded before we started, or about:blank/srcdoc filled by script
      link = this.links.get(iframe);
    }
    if (!link) return null;
    if (link.kind === 'local') {
      if (!this.alive(link)) { this.detach(link); this.links.delete(iframe); return null; }
      return link.agent.text();
    }
    return link.text;
  };

  /** True if some frame's document holds the sentence (same-origin frames answer now) */
  highlightWord(info: WordHighlightInfo): boolean {
    const found = this.dispatch((l) => l.agent.highlightWord(info), { rv: RV_FRAME, kind: 'highlightWord', info });
    return found;
  }

  highlightSentence(sentence: string): boolean {
    return this.dispatch((l) => l.agent.highlightSentence(sentence), { rv: RV_FRAME, kind: 'highlightSentence', sentence });
  }

  clear(): void {
    this.active = null;
    this.links.forEach((l) => {
      if (l.kind === 'local') { if (this.alive(l)) l.agent.clear(); }
      else this.post(l, { rv: RV_FRAME, kind: 'clear' });
    });
  }

  setStyle(style: HighlightStyle): void {
    this.style = { ...this.style, ...style };
    this.links.forEach((l) => this.sendStyle(l));
  }

  setAutoScroll(on: boolean): void {
    this.opts.autoScroll = on;
    this.links.forEach((l) => this.sendStyle(l));
  }

  setClickListen(on: boolean): void {
    this.clickListen = on;
    this.links.forEach((l) => {
      if (l.kind === 'local') { if (this.alive(l)) l.agent.setClickListen(on); }
      else this.post(l, { rv: RV_FRAME, kind: 'clickListen', on });
    });
  }

  /** After reading a frame's selection, clear it there (so it doesn't cover the highlight) */
  clearSelections(): void {
    this.links.forEach((l) => {
      if (l.kind === 'local') { if (this.alive(l)) l.agent.clearSelection(); }
      else this.post(l, { rv: RV_FRAME, kind: 'clearSelection' });
    });
  }

  // ---------------------------------------------------------------------------

  /**
   * Highlight in the frame holding the sentence. Same-origin frames are tried
   * synchronously (the active one first); remote frames get the message and
   * highlight if the sentence is theirs.
   */
  private dispatch(local: (l: Extract<Link, { kind: 'local' }>) => boolean, msg: ToChild): boolean {
    const order = Array.from(this.links.values());
    if (this.active) order.sort((a) => (a === this.active ? -1 : 0));
    for (const l of order) {
      if (l.kind !== 'local' || !this.alive(l)) continue;
      if (local(l)) {
        if (this.active && this.active !== l) this.clearLink(this.active);
        this.active = l;
        this.scrollFrameIntoView(l.iframe);
        return true;
      }
    }
    let remote = false;
    this.links.forEach((l) => { if (l.kind === 'remote') { this.post(l, msg); remote = true; } });
    if (this.active?.kind === 'local') { this.clearLink(this.active); this.active = null; }
    // Remote frames answer asynchronously (each highlights only its own sentences)
    void remote;
    return false;
  }

  private clearLink(l: Link): void {
    if (l.kind === 'local') { if (this.alive(l)) l.agent.clear(); }
    else this.post(l, { rv: RV_FRAME, kind: 'clear' });
  }

  private watch(iframe: HTMLIFrameElement): void {
    if (this.watched.has(iframe)) return;
    this.watched.add(iframe);
    iframe.addEventListener('load', () => {
      const old = this.links.get(iframe);
      if (old?.kind === 'local') { this.detach(old); this.links.delete(iframe); }
      this.attachLocal(iframe);
    });
    this.attachLocal(iframe);
  }

  /** Same-origin frame with a document: run an agent on it */
  private attachLocal(iframe: HTMLIFrameElement): void {
    if (this.links.has(iframe)) return;
    let doc: Document | null = null;
    try { doc = iframe.contentDocument; } catch { doc = null; } // cross-origin
    if (!doc || !doc.body || doc.readyState === 'loading') return;
    if (iframe.offsetWidth < MIN_W || iframe.offsetHeight < MIN_H) return;
    if ((doc.body.textContent || '').trim().length === 0 && doc.body.children.length === 0) return;
    try {
      const agent = new FrameAgent(doc, {
        text: () => { /* read on demand; Play re-reads the page */ },
        selection: (text, rect) => this.opts.onSelection(text, rect ? this.toPage(iframe, rect) : null),
        speak: (text) => this.opts.onSpeak(text),
      }, { autoScroll: this.opts.autoScroll, ignoreSelectors: this.opts.ignoreSelectors });
      const link: Link = { kind: 'local', iframe, agent, doc };
      this.links.set(iframe, link);
      this.sendStyle(link);
      if (this.clickListen) agent.setClickListen(true);
      this.log('Reading same-origin frame', iframe.src || '(inline)');
    } catch (e) {
      this.log('Frame not readable:', (e as Error).message);
    }
  }

  /**
   * Frames on allowed origins whose widget gave up waiting for this one (it
   * started late): ask them to join. Frames without ReadingVox ignore it.
   */
  private inviteFrames(): void {
    document.querySelectorAll('iframe').forEach((iframe) => {
      if (this.links.has(iframe) || !iframe.src) return;
      let origin: string;
      try { origin = new URL(iframe.src, location.href).origin; } catch { return; }
      if (origin === location.origin || !originAllowed(origin, location.hostname, this.opts.frameOrigins)) return;
      try {
        iframe.contentWindow?.postMessage({ rv: RV_FRAME, v: FRAME_PROTOCOL_VERSION, kind: 'invite' } satisfies ToChild, origin);
      } catch { /* not loaded */ }
    });
  }

  private onMessage = (e: MessageEvent): void => {
    if (!isFrameMessage(e.data) || !e.source) return;
    const iframe = Array.from(document.querySelectorAll('iframe')).find((f) => {
      try { return f.contentWindow === e.source; } catch { return false; }
    });
    if (!iframe) return;
    if (!originAllowed(e.origin, location.hostname, this.opts.frameOrigins)) {
      this.log('Ignoring frame message from', e.origin);
      return;
    }
    const msg = e.data as ToParent;
    const link = this.links.get(iframe);

    if (msg.kind === 'hello') {
      if ((msg as { v?: number }).v !== FRAME_PROTOCOL_VERSION) return;
      if (link?.kind === 'local') return; // already reading it directly
      const remote: Link = { kind: 'remote', iframe, win: e.source as Window, origin: e.origin, text: '' };
      this.links.set(iframe, remote);
      this.watched.add(iframe);
      this.post(remote, { rv: RV_FRAME, kind: 'ack' });
      this.sendStyle(remote);
      if (this.clickListen) this.post(remote, { rv: RV_FRAME, kind: 'clickListen', on: true });
      this.log('Frame mode with', e.origin);
      return;
    }
    if (link?.kind !== 'remote' || link.origin !== e.origin) return;
    if (msg.kind === 'text' && typeof msg.text === 'string') {
      link.text = msg.text.slice(0, 200_000);
    } else if (msg.kind === 'selection' && typeof msg.text === 'string') {
      this.opts.onSelection(msg.text.slice(0, 20_000), msg.rect ? this.toPage(iframe, msg.rect) : null);
    } else if (msg.kind === 'speak' && typeof msg.text === 'string' && msg.text.trim()) {
      this.opts.onSpeak(msg.text.slice(0, 200_000));
    }
  };

  private post(l: Extract<Link, { kind: 'remote' }>, msg: ToChild): void {
    try { l.win.postMessage(msg, l.origin); } catch { /* frame gone */ }
  }

  private sendStyle(l: Link): void {
    if (l.kind === 'local') { if (this.alive(l)) l.agent.setStyle(this.style, this.opts.autoScroll); }
    else this.post(l, { rv: RV_FRAME, kind: 'style', style: this.style, autoScroll: this.opts.autoScroll });
  }

  private alive(l: Extract<Link, { kind: 'local' }>): boolean {
    try { return l.iframe.isConnected && l.iframe.contentDocument === l.doc; } catch { return false; }
  }

  private detach(l: Link): void {
    if (l.kind === 'local') l.agent.destroy();
    if (this.active === l) this.active = null;
  }

  /** Frame viewport rect → page viewport rect */
  private toPage(iframe: HTMLIFrameElement, r: FrameRect): FrameRect {
    const box = iframe.getBoundingClientRect();
    return { left: box.left + iframe.clientLeft + r.left, top: box.top + iframe.clientTop + r.top, width: r.width, height: r.height };
  }

  private scrollFrameIntoView(iframe: HTMLIFrameElement): void {
    if (!this.opts.autoScroll) return;
    const r = iframe.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) iframe.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private log(...args: unknown[]): void {
    if (this.opts.debug) console.log('[ReadingVox frames]', ...args);
  }
}
